import { Layout } from "@layouts";
import { PageBreadcrumbs } from "@molecules";
import { useState, useContext, type CSSProperties } from "react";
import { AuthContext } from "@auth";
import { Input as SInput } from "@shadcn";
import { Bar } from "@organisms";
import { useUpdatePartnerBoostedRewards } from "@mutations";
import { IofferBoost } from "@api";
import * as RSwitch from "@radix-ui/react-switch";
import "./styles.css";
import { PagePaths } from "../../../types";
import PhoneRewardsImage from "../../../assets/phone_rewards.jpg";
import { useTranslation } from "react-i18next";

const HERO_SECTION_HEIGHT = 288;
const colors = require("@theme/colors") as Record<string, any>;
const switchCheckedStyle = {
  "--switch-checked-bg": colors.primary.medium,
} as CSSProperties;

export type CashbackRewardsPageTemplateProps = {
  premiumUsers: any[];
  navigateToPage: (index: number, to: string) => void;
  onReturnPressed: () => void;
};

export const CashbackRewardsPageTemplate = ({
  premiumUsers,
  navigateToPage,
  onReturnPressed,
}: CashbackRewardsPageTemplateProps): JSX.Element => {
  const { t } = useTranslation();
  const { partner, isLoadingData, setIsLoadingData } = useContext(AuthContext);
  const updatePartnerBoostedRewards = useUpdatePartnerBoostedRewards();
  const isBoostLimitAlreadyActive = partner?.offerAmountsManagement?.offerBoost
    ?.boostLimitPerUser !== null && partner?.offerAmountsManagement?.offerBoost
    ?.boostLimitPerUser !== undefined
    ? true
    : false;
  const [boostedAmount, setBoostedAmount] = useState<number | null | undefined>(
    partner?.offerAmountsManagement?.offerBoost?.boostAmount ?? undefined,
  ); // This will have the percent value stored
  const [boostLimitAmount, setBoostLimitAmount] = useState<number | undefined>(
    partner?.offerAmountsManagement?.offerBoost?.boostLimitPerUser ?? undefined,
  ); // This will have the boost spending limit
  const [boostActivated, setIsBoostActivated] = useState<boolean>(
    partner?.offerAmountsManagement?.offerBoost?.isBoostingAllOffers ?? false,
  ); // This will have to have init val matching the backend stored value
  const [boostLimitActivated, setBoostLimitActivated] = useState<boolean>(
    isBoostLimitAlreadyActive,
  ); // This will have to have init val matching the backend stored value

  const isBoostAmountIdenticalToDB = boostedAmount ===
  partner?.offerAmountsManagement?.offerBoost?.boostAmount;
  const isBoostActivatedIdenticalToDB = boostActivated ===
  partner?.offerAmountsManagement?.offerBoost?.isBoostingAllOffers || (boostActivated === false && !partner?.offerAmountsManagement?.offerBoost?.isBoostingAllOffers);
  const isBoostLimitActiveIdenticalToDB = (boostLimitAmount ===
    partner?.offerAmountsManagement?.offerBoost?.boostLimitPerUser || (!boostLimitAmount && partner?.offerAmountsManagement?.offerBoost?.boostLimitPerUser === null));
  const isValuesIdenticalToDB =
  isBoostAmountIdenticalToDB &&
  isBoostActivatedIdenticalToDB &&
  isBoostLimitActiveIdenticalToDB;

  console.log(isBoostAmountIdenticalToDB + "\n" + isBoostActivatedIdenticalToDB + "\n" + isBoostLimitActiveIdenticalToDB + boostLimitAmount + partner?.offerAmountsManagement?.offerBoost?.boostLimitPerUser);

  const onBoostChange = () => {
    if (boostActivated) {
      setIsBoostActivated(false);
      setBoostLimitAmount(undefined);
      setBoostLimitActivated(false);
    } else {
      setIsBoostActivated(true);
      setBoostedAmount(partner?.offerAmountsManagement?.offerBoost?.boostAmount ?? 5)
    }
  };

  const onBoostLimitChange = () => {
    if (boostLimitActivated) {
      setBoostLimitActivated(false);
      setBoostLimitAmount(undefined);
    } else {
      setBoostLimitActivated(true);
      setBoostLimitAmount(100);
    }
  };

  const onPressConfirmBoostChoices = async () => {
    setIsLoadingData(true);
    const offerBoost: IofferBoost = {
      boostAmount: boostedAmount ?? 0,
      isBoostingAllOffers: boostActivated,
      boostLimitPerUser: boostLimitAmount,
    }
    await updatePartnerBoostedRewards(offerBoost);
  };

  const goToPerks = () => navigateToPage(3, `/dashboard/${PagePaths.perks}`);

  return (
    <Layout
      variant="dashboard"
      header={t("cashbackRewards.header")}
      subheader={
        <div className="flex flex-row items-start justify-between max-w-5xl">
          <div className="page-subtitle mt-2">
            {t("cashbackRewards.subheader")}{" "}
            {/* <span className="text-brand-orange">(*Beta Version)</span> */}
          </div>
          {/* <div className="pr-4">
            <Item variant="beta" />
          </div> */}
        </div>
      }
    >
      <div className="p-1 rounded-md max-w-5xl">
        <PageBreadcrumbs
          items={[
            { label: t("cashbackRewards.breadcrumbs.benefits"), onClick: goToPerks },
            { label: t("cashbackRewards.breadcrumbs.cashback") },
          ]}
        />
        <div className="flex flex-col gap-6">
          <div
            className="flex flex-col md:flex-row items-center md:items-start gap-6 max-w-5xl border border-neutral-light rounded-2xl p-6 bg-white"
            style={{ minHeight: HERO_SECTION_HEIGHT }}
          >
            <img
              src={PhoneRewardsImage}
              alt={t("cashbackRewards.hero.alt")}
              className="w-28 md:w-40 lg:w-40 lg:min-w-40 lg:max-w-40 h-auto rounded-xl border border-neutral-medium"
            />
            <div className="flex flex-col text-left space-y-3">
              <p className="text-sm font-semibold text-neutral-mediumDark uppercase tracking-wide">
                {t("cashbackRewards.hero.kicker")}
              </p>
              <h3 className="text-neutral-darkest text-2xl font-semibold">
                {t("cashbackRewards.hero.title")}
              </h3>
              <p className="text-neutral-dark text-base font-normal">
                {t("cashbackRewards.hero.description")}
              </p>
            </div>
          </div>
          <div className="w-full max-w-5xl space-y-4">
            <div className="bg-white border border-neutral-light rounded-xl p-5">
              <div className="flex flex-row items-center gap-2 mt-1">
                <h2 className="section-title flex items-center gap-2">
                  {/* <Trophy size={18} /> */}
                  {t("cashbackRewards.boostSection.title")}
                </h2>
              </div>
              <div className="flex flex-row my-2 mt-4 items-center">
                <RSwitch.Root
                  style={switchCheckedStyle}
                  className="SwitchRoot"
                  id="boost-activated"
                  checked={boostActivated}
                  onCheckedChange={onBoostChange}
                >
                  <RSwitch.Thumb className="SwitchThumb" />
                </RSwitch.Root>
                <div className="ml-2 flex flex-row items-center text-sm">
                  {t("cashbackRewards.boostSection.boostOffersBy")}
                  <span className="flex flex-row">
                    <SInput
                      type="number"
                      lang="en-US"
                      placeholder={t("cashbackRewards.boostSection.amountPlaceholder")}
                      step="1"
                      value={boostedAmount || 5}
                      onChange={(event) => {
                        setBoostedAmount(Number(event?.target.value));
                      }}
                      className="h-12 w-16 bg-white rounded-md ml-2 text-lg"
                    />
                  </span>
                  <div className="ml-1">%</div>
                </div>
              </div>
              <div className="text-sm pb-2">
                {t("cashbackRewards.boostSection.helperBoost")}
              </div>
              <div className="flex flex-row mb-2 mt-6 items-center">
                <RSwitch.Root
                  style={switchCheckedStyle}
                  className="SwitchRoot"
                  id="boost-limit"
                  disabled={false}
                  checked={boostLimitActivated}
                  onCheckedChange={onBoostLimitChange}
                >
                  <RSwitch.Thumb className="SwitchThumb" />
                </RSwitch.Root>
                <div className="ml-2 flex flex-row items-center text-sm">
                  {t("cashbackRewards.boostSection.boostLimitLabel")}
                  <SInput
                    type="number"
                    lang="en-US"
                    placeholder={t("cashbackRewards.boostSection.boostLimitPlaceholder")}
                    step="1"
                    min={100}
                    value={boostLimitAmount || 100}
                    onChange={(event) => {
                      setBoostLimitAmount(Number(event?.target.value));
                    }}
                    disabled={!boostLimitActivated}
                    className="h-12 w-20 bg-white rounded-md ml-2 text-lg"
                  />
                </div>
              </div>
              <div className="text-sm">
                {t("cashbackRewards.boostSection.helperLimit")}
              </div>

              <div className="w-64 pt-10">
                <button
                  onClick={onPressConfirmBoostChoices}
                  disabled={isValuesIdenticalToDB || isLoadingData}
                  className={
                    isValuesIdenticalToDB || isLoadingData
                      ? "w-full px-4 py-2.5 rounded-full font-medium text-sm transition-colors border bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                      : "w-full px-4 py-2.5 rounded-full font-medium text-sm text-white bg-primary-medium hover:bg-primary-dark transition-colors"
                  }
                >
                  {isLoadingData ? t("cashbackRewards.boostSection.saving") : t("cashbackRewards.boostSection.confirm")}
                </button>
              </div>
            </div>
          </div>

        </div>
        {/* <div className="pt-1 mb-16">
          <BlogSection page={"cashbackRewards"} />
        </div> */}
        <Bar
          variant="bottom"
          onReturnPressed={onReturnPressed}
          onContinuePressed={null}
          isContinueDisabled={true}
        />
      </div>
    </Layout>
  );
};
